import { motion } from "framer-motion";
import { Zap, Truck, Plug, Snowflake, UtensilsCrossed } from "lucide-react";
import { IMAGES } from "@/lib/data";
import { galleryImageUrl, useGalleryImages } from "@/lib/gallery";

const EASE = [0.22, 1, 0.36, 1];

const SPECS = [
    { icon: Plug, label: "Strøm", value: "230V stik med jord — eller CEE 16A" },
    { icon: Truck, label: "Plads", value: "Ca. 2 × 1 m — kræver fri adgang" },
    { icon: Snowflake, label: "Medfølger", value: "Emhætte, køl & grill" },
    { icon: UtensilsCrossed, label: "Opsætning", value: "Vi leverer, stiller op og serverer" },
];

const FALLBACK_GALLERY = [
    { src: IMAGES.grill, alt: "Pølser på grillen under et event" },
    { src: IMAGES.event1, alt: "Stemning fra tidligere event med madvogn" },
    { src: IMAGES.event2, alt: "Gæster samlet om madvognen til festen" },
];

const ROTATIONS = ["-rotate-2", "rotate-1", "-rotate-1"];

export default function WagonSection() {
    const { images } = useGalleryImages();
    const gallery = images.length
        ? images.map((img) => ({
              id: img.id,
              src: galleryImageUrl(img),
              alt: img.description || "Billede fra tidligere event",
          }))
        : FALLBACK_GALLERY.map((g, i) => ({ id: `stock-${i}`, src: g.src, alt: g.alt }));

    return (
        <section id="vognen" data-testid="wagon-section" className="relative border-t border-border bg-paper py-28 sm:py-36">
            <div className="mx-auto grid max-w-6xl gap-16 px-5 lg:grid-cols-12">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="lg:col-span-5"
                >
                    <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-poelse">03 — Vognen & strøm</p>
                    <h2 className="font-serif text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
                        Alt hvad vognen kræver? En klar løsning på strømmen.
                    </h2>
                    <p className="mt-4 text-base text-muted-foreground sm:text-lg">
                        Vi klarer resten. Vognen ruller ind i haven, indkørslen eller på
                        parkeringspladsen — og så dufter der af ristede pølser få minutter efter.
                    </p>
                    <div
                        data-testid="power-warning"
                        className="mt-8 flex items-start gap-4 rounded-xl border-2 border-dashed border-rust bg-gold/25 p-5"
                    >
                        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-rust text-paper">
                            <Zap className="h-5 w-5" />
                        </span>
                        <div>
                            <p className="font-semibold text-ink">Husk et stik med jord!</p>
                            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                                Emhætterne brænder sammen uden. Sørg for et almindeligt 230V stik med
                                jord, eller et CEE 16A kraftstik, der hvor vognen skal stå. Har du
                                ingen af delene, leverer vi en gasvogn, der kun kræver 220 volt og
                                13 ampere. Adapterkabel, forlængerledning og kabeltromle kan tilvælges.
                            </p>
                        </div>
                    </div>
                    <ul className="mt-10 divide-y divide-border">
                        {SPECS.map((s) => (
                            <li key={s.label} className="flex items-center gap-4 py-4">
                                <s.icon className="h-5 w-5 shrink-0 text-poelse" />
                                <span className="w-24 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
                                    {s.label}
                                </span>
                                <span className="text-sm font-medium text-ink">{s.value}</span>
                            </li>
                        ))}
                    </ul>
                </motion.div>
                <div className="lg:col-span-7">
                    <p className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                        Fra tidligere events
                    </p>
                    <div className="grid grid-cols-3 gap-4 sm:gap-6">
                        {gallery.map((g, i) => (
                            <motion.div
                                key={g.id}
                                data-testid={`gallery-image-${i}`}
                                initial={{ opacity: 0, y: 40, rotate: 0 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: (i % 3) * 0.12, ease: EASE }}
                                className={`${ROTATIONS[i % 3]} border-4 border-background bg-background shadow-[0_16px_40px_rgba(28,25,23,0.14)] transition-[transform] duration-500 hover:rotate-0 hover:scale-[1.04] ${
                                    i % 3 === 1 ? "translate-y-6" : ""
                                }`}
                            >
                                <img src={g.src} alt={g.alt} loading="lazy" className="aspect-[3/4] w-full object-cover" />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
